import CountUp from 'react-countup'
import { formatNumber } from '../format'
import i18n from './i18n'
import type { Amount, Locales } from '@captaincause/types'
import type { ReactNode } from 'react'
import type { CountUpProps as ReactCountUpProps } from 'react-countup'

export type CountUpProps = Omit<ReactCountUpProps, 'end'>

const CURRENCIES = {
  EUR: {
    symbol: ' €',
    transform: function transform(
      amount: number,
      locale: Locales,
      withSymbol: boolean = true,
    ): string {
      return `${formatNumber(amount, locale)}${withSymbol ? this.symbol : ''}`
    },
    CountUp: function countup(
      end: number,
      locale: Locales,
      numberOnly?: boolean,
      countUpOptions?: CountUpProps,
    ): ReactNode {
      if (end % 1 !== 0) {
        return `${formatNumber(end, locale)}${numberOnly ? '' : this.symbol}`
      }

      return (
        <CountUp
          end={end}
          suffix={numberOnly ? '' : this.symbol}
          delay={0.5}
          duration={end < 10 ? 1 : 1.5}
          preserveValue
          separator={i18n[locale]['number.separator']}
          {...countUpOptions}
        />
      )
    },
  },
}

export function wholeAmount(amount: Amount, rounded: boolean = false) {
  const nb = amount.amount / 100

  return rounded ? Math.round(nb) : nb
}

export function computeAmount(
  amount: Amount,
  locale: Locales,
  rounded: boolean = false,
  withSymbol: boolean = true,
): string {
  return CURRENCIES[amount.currency].transform(
    wholeAmount(amount, rounded),
    locale,
    withSymbol,
  )
}

export function countUpAmount({
  amount,
  locale,
  numberOnly,
  countUpOptions,
}: {
  amount: Amount
  locale: Locales
  numberOnly?: boolean
  countUpOptions?: CountUpProps
}): ReactNode {
  return CURRENCIES[amount.currency].CountUp(
    wholeAmount(amount),
    locale,
    numberOnly,
    countUpOptions,
  )
}

export function countUpValue({
  value,
  locale,
  countUpOptions,
}: {
  value: number
  locale: Locales
  countUpOptions?: CountUpProps
}): ReactNode {
  if (value % 1 !== 0) {
    return formatNumber(value, locale)
  }

  return (
    <CountUp
      end={value}
      delay={0.5}
      duration={value > 500 ? 2 : 1.5}
      preserveValue
      separator={i18n[locale]['number.separator']}
      {...countUpOptions}
    />
  )
}

export function computePointsAmount(
  amount: Amount,
  pointsValueInCents?: number | null,
): number {
  if (pointsValueInCents === null || pointsValueInCents === undefined) {
    return amount.amount
  }

  return Math.round(amount.amount * pointsValueInCents)
}
